import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Button, MessageBar, MessageBarBody, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell,
  TableRow, makeStyles, tokens,
} from '@fluentui/react-components';
import { BotSparkleRegular } from '@fluentui/react-icons';
import { useIsStateTaxAdmin } from '../../services/stateTaxClientsApi';
import { useReadinessBoard } from '../../services/complianceApi';
import { apiErrorMessage } from '../../lib/apiErrorMessage';

// Grading loop P3 (docs/superpowers/specs/2026-10-22-suggestion-grading-loop-design.md §8): the automation-readiness board,
// Christian's periodic review instrument. ADMINS ONLY. The app's automated estimate is a shadow system graded against what
// was filed; this card shows how close each client is to the (PROPOSED) streak that would make it a candidate for
// automated filing. It never decides anything: the server computes every figure (ReadinessBoard.Build), and "no ground
// truth" reads "not graded", never 100%.

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  summary: { color: tokens.colorNeutralForeground2 },
  subTitle: { fontWeight: tokens.fontWeightSemibold },
  tableCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflowX: 'auto',
  },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  muted: { color: tokens.colorNeutralForeground3 },
});

const FREQUENCY_ORDER = ['monthly', 'quarterly', 'semiannual', 'annual'];

export default function ReadinessCard() {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { data: isAdmin = false } = useIsStateTaxAdmin();
  const { data, isLoading, isError, error } = useReadinessBoard(isAdmin);
  const [showUngraded, setShowUngraded] = useState(false);

  if (!isAdmin) return null;

  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const month = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }) : t('common.dash');
  const pct = (r: number | null) =>
    r === null ? t('managementPage.readiness.notGraded') : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(r);
  const freqLabel = (f: string | null) => (f ? t(`managementPage.readiness.frequency.${f}`, { defaultValue: f }) : t('common.dash'));
  // A table cell starts a sentence of its own, so it takes the capitalised form; freqLabel stays for mid-sentence use.
  const freqCell = (f: string | null) => (f ? t(`managementPage.readiness.frequencyCell.${f}`, { defaultValue: f }) : t('common.dash'));
  // Miss classes are the classifier's codes (GradeClassifier); staff read the words, the code stays in the tooltip.
  const missLabel = (c: string) => t(`managementPage.readiness.missClass.${c}`, { defaultValue: c });
  const periodLabel = (p: string) => (/^\d{4}-\d{2}$/.test(p) ? month(`${p}-01`) : p);
  const figureLabel = (f: string) => t(`managementPage.readiness.figure.${f}`, { defaultValue: f });

  const clients = data?.clients ?? [];
  // Until a filing is graded, every row reads "not graded": one sentence says that, and the list waits behind a button.
  const noneGraded = clients.length > 0 && clients.every((c) => c.gradedFilings === 0);
  const series = FREQUENCY_ORDER.filter((f) => (data?.trendByFrequency?.[f] ?? []).length > 0);

  return (
    <section className={styles.section} data-testid="readiness-card">
      <div className={styles.sectionTitle}>
        <BotSparkleRegular />
        {t('managementPage.readiness.title')}
      </div>
      <span className={styles.summary}>{t('managementPage.readiness.intro')}</span>
      {data && !data.ruleIsProposed && (
        <span className={styles.summary} data-testid="readiness-rule">
          {t('managementPage.readiness.ruled', { n: data.n.monthly })}
        </span>
      )}
      {data?.ruleIsProposed && (
        <MessageBar intent="info">
          <MessageBarBody>
            {t('managementPage.readiness.proposed', {
              monthly: data.n.monthly, quarterly: data.n.quarterly, annual: data.n.annual,
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{apiErrorMessage(error, t, 'managementPage.readiness.loadError')}</MessageBarBody>
        </MessageBar>
      )}
      {isLoading && <Spinner label={t('common.loading')} />}
      {!isLoading && !isError && clients.length === 0 && (
        <span className={styles.muted} data-testid="readiness-empty">{t('managementPage.readiness.empty')}</span>
      )}
      {!isLoading && !isError && noneGraded && (
        <div className={styles.muted} data-testid="readiness-none-graded">
          {t('managementPage.readiness.noneGraded', { count: clients.length })}{' '}
          <Button appearance="transparent" size="small" onClick={() => setShowUngraded((v) => !v)} data-testid="readiness-toggle">
            {showUngraded ? t('managementPage.readiness.hideClients') : t('managementPage.readiness.showClients', { count: clients.length })}
          </Button>
        </div>
      )}
      {!isLoading && !isError && clients.length > 0 && (!noneGraded || showUngraded) && (
        <div className={styles.tableCard}>
          <Table size="small" aria-label={t('managementPage.readiness.title')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('managementPage.readiness.columns.client')}</TableHeaderCell>
                <TableHeaderCell>{t('managementPage.readiness.columns.frequency')}</TableHeaderCell>
                <TableHeaderCell>{t('managementPage.readiness.columns.streak')}</TableHeaderCell>
                <TableHeaderCell>{t('managementPage.readiness.columns.toGo')}</TableHeaderCell>
                <TableHeaderCell>{t('managementPage.readiness.columns.lastMiss')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.clientId} data-testid="readiness-row">
                  <TableCell>
                    {c.clientName ?? c.clientId}{' '}
                    {c.eligible && <Badge appearance="tint" color="success">{t('managementPage.readiness.eligible')}</Badge>}
                  </TableCell>
                  <TableCell>{freqCell(c.frequency)}</TableCell>
                  <TableCell className={styles.num}>
                    {c.gradedFilings === 0 ? t('managementPage.readiness.notGraded') : t('managementPage.readiness.streak', { count: c.streak })}
                  </TableCell>
                  <TableCell>
                    {c.filingsToEligibility === null
                      ? t('common.dash')
                      : c.filingsToEligibility === 0
                        ? t('managementPage.readiness.reached')
                        : t('managementPage.readiness.toGo', { count: c.filingsToEligibility, month: month(c.etaMonth) })}
                  </TableCell>
                  <TableCell>
                    {c.lastMissPeriod
                      ? t('managementPage.readiness.lastMiss', {
                          period: periodLabel(c.lastMissPeriod),
                          figures: c.lastMissFigures.map(figureLabel).join(', '),
                          cls: c.lastMissClass ? missLabel(c.lastMissClass) : t('managementPage.readiness.unclassified'),
                        })
                      : t('common.dash')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!isLoading && !isError && series.map((f) => (
        <div key={f} className={styles.section}>
          <span className={styles.subTitle}>{t('managementPage.readiness.trendTitle', { frequency: freqLabel(f) })}</span>
          <div className={styles.tableCard}>
            <Table size="small" aria-label={t('managementPage.readiness.trendTitle', { frequency: freqLabel(f) })}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.readiness.columns.period')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.readiness.columns.graded')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.readiness.columns.exactRate')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.readiness.columns.topMiss')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.trendByFrequency[f] ?? []).map((p) => (
                  <TableRow key={p.period} data-testid="readiness-trend-row">
                    <TableCell>{periodLabel(p.period)}{p.reconstructed ? ' *' : ''}</TableCell>
                    <TableCell className={styles.num}>{p.graded}</TableCell>
                    <TableCell className={styles.num}>{pct(p.exactRate)}</TableCell>
                    <TableCell title={p.topMissClass ?? undefined}>
                      {p.topMissClass ? missLabel(p.topMissClass) : t('common.dash')}
                      {p.answer ? ` (${t('managementPage.readiness.answeredBy', { answer: p.answer })})` : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(data?.trendByFrequency[f] ?? []).some((p) => p.reconstructed) && (
            <span className={styles.muted}>{`* ${t('managementPage.readiness.reconstructedNote')}`}</span>
          )}
        </div>
      ))}
    </section>
  );
}
